/**
 * Release Testers List
 *
 * Table of testers for a Release QA run with expandable rows
 */

import React from 'react';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import { DataTable, LoadingOrbit, Text, TagBadge, badgeVariantToTagColor } from '@chatsdet/ui';
import { Robot } from '@phosphor-icons/react';
import { useReleaseRunTesters } from '../useReleases';
import type { ReleaseTesterItem, ReleaseQATesterStatus, ReleaseQATodoItem } from '../releaseTypes';
import { getReleaseTesterStatusMeta, computeTestStats, formatTestStats } from '../releaseTypes';

const formatDuration = (
  startedAt: string | null | undefined,
  finishedAt: string | null | undefined
): string => {
  if (!startedAt) return '-';
  const start = new Date(startedAt);
  const end = finishedAt ? new Date(finishedAt) : new Date();
  const durationMs = end.getTime() - start.getTime();

  if (durationMs < 1000) return '<1s';
  if (durationMs < 60000) return `${Math.round(durationMs / 1000)}s`;
  if (durationMs < 3600000) return `${Math.round(durationMs / 60000)}m`;
  return `${Math.round(durationMs / 3600000)}h`;
};

const TesterStatusBadge: React.FC<{ status: ReleaseQATesterStatus }> = ({ status }) => {
  const meta = getReleaseTesterStatusMeta(status);
  const variantMap: Record<
    string,
    'success' | 'secondary' | 'warning' | 'destructive' | 'outline'
  > = {
    green: 'success',
    blue: 'secondary',
    yellow: 'warning',
    red: 'destructive',
    gray: 'outline',
  };
  return (
    <TagBadge
      name={meta.label}
      color={badgeVariantToTagColor(variantMap[meta.color] ?? 'outline')}
      hideDot
      size="sm"
    />
  );
};

const TodoSummary: React.FC<{ todoItems: ReleaseQATodoItem[] | null }> = ({ todoItems }) => {
  const stats = computeTestStats(todoItems);
  if (stats.total === 0)
    return (
      <Text size="sm" className="text-muted-foreground">
        -
      </Text>
    );

  return (
    <Text size="sm" className={stats.unhealthy > 0 ? 'text-destructive' : 'text-muted-foreground'}>
      {formatTestStats(stats)}
    </Text>
  );
};

const TodoList: React.FC<{ todoItems: ReleaseQATodoItem[] }> = ({ todoItems }) => {
  const getStatusIcon = (status: ReleaseQATodoItem['status']) => {
    switch (status) {
      case 'healthy':
        return <span className="text-green-500">&#10003;</span>;
      case 'unhealthy':
        return <span className="text-destructive">&#10005;</span>;
      case 'not_applicable':
        return <span className="text-muted-foreground">-</span>;
      default:
        return <span className="text-muted-foreground">&#8230;</span>;
    }
  };

  return (
    <div className="space-y-1">
      {todoItems.map((item) => (
        <div key={item.id} className="flex items-start gap-2">
          <span className="w-4 flex-shrink-0 text-center">{getStatusIcon(item.status)}</span>
          <div className="flex-1 min-w-0">
            <Text size="sm">{item.item}</Text>
            {item.summary && (
              <Text size="xs" className="text-muted-foreground mt-0.5">
                {item.summary}
              </Text>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

type ReleaseTesterListProps = {
  releaseRunId: string;
};

export const ReleaseTestersList: React.FC<ReleaseTesterListProps> = ({ releaseRunId }) => {
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(20);

  const testersQuery = useReleaseRunTesters(releaseRunId, { page, pageSize });

  const columns: ColumnDef<StockFeatures, ReleaseTesterItem>[] = React.useMemo(
    () => [
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => <TesterStatusBadge status={row.original.status} />,
        meta: { flex: 0.8 },
      },
      {
        id: 'area',
        header: 'Test Area',
        cell: ({ row }) => (
          <Text size="sm" className="truncate">
            {row.original.testAreaName ?? 'General'}
          </Text>
        ),
        meta: { flex: 2 },
      },
      {
        id: 'tests',
        header: 'Tests',
        cell: ({ row }) => <TodoSummary todoItems={row.original.todoItems} />,
        meta: { flex: 0.7 },
      },
      {
        accessorKey: 'credentialName',
        header: 'Credential',
        cell: ({ row }) => (
          <Text size="sm" className="text-muted-foreground truncate">
            {row.original.credentialName ?? '-'}
          </Text>
        ),
        meta: { flex: 1 },
      },
      {
        id: 'health',
        header: 'Health',
        cell: ({ row }) => {
          const health = row.original.healthStatus;
          if (!health)
            return (
              <Text size="sm" className="text-muted-foreground">
                -
              </Text>
            );

          const variantMap: Record<string, 'success' | 'warning' | 'destructive'> = {
            healthy: 'success',
            mixed: 'warning',
            unhealthy: 'destructive',
          };
          return (
            <TagBadge
              name={health}
              color={badgeVariantToTagColor(variantMap[health] ?? 'outline')}
              hideDot
              size="sm"
            />
          );
        },
        meta: { flex: 0.7 },
      },
      {
        accessorKey: 'issuesFound',
        header: 'Issues',
        cell: ({ row }) => (
          <Text
            size="sm"
            className={row.original.issuesFound > 0 ? 'text-destructive' : 'text-muted-foreground'}
          >
            {row.original.issuesFound}
          </Text>
        ),
        meta: { flex: 0.5 },
      },
      {
        id: 'duration',
        header: 'Duration',
        cell: ({ row }) => (
          <Text size="sm" className="text-muted-foreground">
            {formatDuration(row.original.startedAt, row.original.finishedAt)}
          </Text>
        ),
        meta: { flex: 0.8 },
      },
    ],
    []
  );

  if (testersQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingOrbit size="lg" />
      </div>
    );
  }

  if (!testersQuery.data?.testers.length) {
    return (
      <div className="text-center py-12 border-2 border-dashed border-border rounded-lg">
        <Robot size={48} className="mx-auto text-muted-foreground mb-4" />
        <Text className="text-muted-foreground">No testers yet</Text>
        <Text size="sm" className="text-muted-foreground mt-1">
          Testers will appear here once the planning phase completes
        </Text>
      </div>
    );
  }

  return (
    <DataTable
      columns={columns}
      data={testersQuery.data.testers}
      aria-label="Release testers table"
      manualPagination
      pageCount={testersQuery.data.pagination.totalPages}
      pageIndex={page - 1}
      pageSize={pageSize}
      onPaginationChange={(newPageIndex, newPageSize) => {
        setPage(newPageIndex + 1);
        setPageSize(newPageSize);
      }}
      expandableRows={{
        render: (row) => {
          const tester = row.original;
          const hasTodoItems = tester.todoItems && tester.todoItems.length > 0;

          if (!hasTodoItems) {
            return (
              <div className="py-4 text-center">
                <Text size="sm" className="text-muted-foreground">
                  No test cases recorded for this tester
                </Text>
                {tester.summary && (
                  <Text size="sm" className="text-muted-foreground mt-2">
                    Summary: {tester.summary}
                  </Text>
                )}
              </div>
            );
          }
          return (
            <div className="space-y-4">
              <div>
                <Text size="sm" className="font-medium mb-2">
                  Test Checklist
                </Text>
                <TodoList todoItems={tester.todoItems!} />
              </div>
              {tester.summary && (
                <div className="pt-3 border-t border-border">
                  <Text size="xs" className="text-muted-foreground font-medium">
                    Tester Summary
                  </Text>
                  <Text size="sm" className="mt-1">
                    {tester.summary}
                  </Text>
                </div>
              )}
            </div>
          );
        },
        single: true,
      }}
    />
  );
};
